import React, { useEffect, useState, useMemo, useCallback } from 'react';
import SpinnerLoader from '../../components/spinnerLoader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space, Empty, Avatar, Form, Input, Select, Segmented, message, TimePicker, Calendar, Tooltip } from 'antd';
import { ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined, ClockCircleOutlined, TeamOutlined, CheckCircleFilled, CloseCircleFilled, CalendarOutlined, FileTextOutlined, HistoryOutlined, EnvironmentOutlined, PlusOutlined, InfoCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { batchActions } from '../../actions/batchActions';
import StudentDatePicker from '../../components/studentDatePicker';
import { batchService } from '../../services/batchService';
import GuardedButton from '../../components/guardedButton';
import dayjs from 'dayjs';
import { useCurrentPageAccess } from '../../services/helper';

const normalizeAssignedDateValue = (assignedDate: any) => {
  if (!assignedDate) return null;
  if (typeof assignedDate === 'object' && !Array.isArray(assignedDate)) {
    return assignedDate.date || assignedDate.assignedDate || assignedDate.value || assignedDate.day || null;
  }
  return assignedDate;
};

const getAssignmentDates = (item: any) => {
  const values = Array.isArray(item?.assignedDates)
    ? item.assignedDates
    : (item?.assignedDate ? [item.assignedDate] : (item?.studentId?.assignedDates ? item.studentId.assignedDates : []));
  return (values || []).map((dateValue: any) => normalizeAssignedDateValue(dateValue)).filter(Boolean);
};

const getAssignmentDateKey = (assignedDate: any) => {
  const dateValue = normalizeAssignedDateValue(assignedDate);
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const getAssignmentStudentId = (item: any) => {
  const studentObject = typeof item === 'object' && item.studentId ? item.studentId : item;
  return studentObject?._id || studentObject;
};

const buildAssignedDateEntry = (assignedDate: string, batchData?: any) => ({
  date: assignedDate,
  startTime: batchData?.startTime || undefined,
  endTime: batchData?.endTime || undefined,
});

const BatchView: React.FC<{ role?: string }> = ({ role }) => {

  const navigate = useNavigate();
  const location = useLocation();
  const batchId = location.state?.batchId;
  const dispatch = useDispatch();
  const pageAccess = useCurrentPageAccess();
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [availableInstructors, setAvailableInstructors] = useState<any[]>([]);
  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const [availableStaff, setAvailableStaff] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [attendanceData, setAttendanceData] = useState<Record<string, 'PRESENT' | 'ABSENT' | null>>({});
  const [previousAttendanceData, setPreviousAttendanceData] = useState<Record<string, 'PRESENT' | 'ABSENT' | null>>({});
  const [pendingAttendanceUpdate, setPendingAttendanceUpdate] = useState<string | null>(null);
  const [pendingUserFetch, setPendingUserFetch] = useState<'INSTRUCTOR' | 'STUDENT' | null>(null);
  const [selectedTrainingType, setSelectedTrainingType] = useState<string | null>(null);
  const [editedStudentAssignments, setEditedStudentAssignments] = useState<any[]>([]);
  const [calMonth, setCalMonth] = useState(dayjs());
  const [selectedStudentForView, setSelectedStudentForView] = useState<string | null>(null);

  const viewBatchState = useSelector((state: any) => state.viewBatchReducer);
  const batchData = useSelector((state: any) => state.createBatchReducer);
  const availableUsersState = useSelector((state: any) => state.getAvailableUsersReducer);
  const availableStudentsState = useSelector((state: any) => state.getAvailableStudentsReducer);
  const availableStudentsForBatchState = useSelector((state: any) => state.getAvailableStudentsForBatchReducer);
  const assignStudentWithDateState = useSelector((state: any) => state.assignStudentWithDateReducer);
  const removeStudentFromBatchState = useSelector((state: any) => state.removeStudentFromBatchReducer);
  const markAttendanceState = useSelector((state: any) => state.markAttendanceReducer);

  const batch = viewBatchState?.batchData?.batch || viewBatchState?.batchData || null;
  const isLoading = viewBatchState?.isLoading;
  const isUpdating = batchData?.isLoading;

  // ── live-watch maxStudents in edit mode so the calendar restriction updates instantly ──
  const watchedMaxStudents = Form.useWatch('maxStudents', form);

  const activeStudentAssignments = useMemo(
    () => (isEditMode ? editedStudentAssignments : (batch?.studentIds || [])),
    [isEditMode, editedStudentAssignments, batch?.studentIds]
  );

  // normalize createdAt to a Date object (handles ISO string, ms or seconds timestamp)
  const batchCreatedDateObj: Date | null = (() => {
    const v: any = batch?.createdAt;
    if (!v) return null;
    const num = Number(v);
    if (!Number.isNaN(num)) {
      if (String(v).length === 10) return new Date(num * 1000);
      return new Date(num);
    }
    return new Date(String(v));
  })();

  const fetchAvailableUsersForEdit = useCallback((startTime: string, endTime: string) => {
    setLoadingUsers(true);
    setPendingUserFetch('INSTRUCTOR');
    dispatch(batchActions.getAvailableUsers({ role: 'INSTRUCTOR', startTime, endTime }));
    dispatch(batchActions.getAvailableStudentsRequest({ role: 'STUDENT', startTime, endTime }));
    // Fetch available staff
    batchService.getAvailableUsers({ role: 'STAFF', startTime, endTime })
      .then((res: any) => {
        const staffData = res?.resultObject?.users || res?.users || [];
        setAvailableStaff(staffData);
      })
      .catch(() => setAvailableStaff([]));
  }, [dispatch]);

  useEffect(() => {
    if (batchId) {
      dispatch(batchActions.viewBatch({ id: batchId }));
    }
  }, [dispatch, batchId]);

  const [editUsersFetched, setEditUsersFetched] = useState(false);
  const [expandedAttendanceRows, setExpandedAttendanceRows] = useState<Record<string, boolean>>({});

  const toggleAttendanceRow = useCallback((id: string) => {
    setExpandedAttendanceRows(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  useEffect(() => {
    if (isEditMode && !editUsersFetched && batch?.startTime && batch?.endTime) {
      setEditUsersFetched(true);
      fetchAvailableUsersForEdit(batch.startTime, batch.endTime);
    }
    if (!isEditMode) {
      setEditUsersFetched(false);
    }
  }, [isEditMode, editUsersFetched, batch?.startTime, batch?.endTime, fetchAvailableUsersForEdit]);

  useEffect(() => {
    if (batch && isEditMode) {
      setSelectedTrainingType(batch.trainingType || null);
      setEditedStudentAssignments(Array.isArray(batch.studentIds) ? batch.studentIds : []);
      form.setFieldsValue({
        batchName: batch.batchName,
        instructorId: batch.instructorId?._id || batch.instructorId,
        staffId: batch.staffId?._id || batch.staffId || null,
        trainingType: batch.trainingType || null,
        studentIds: batch.studentIds?.map((s: any) => s._id || s) || [],
        status: batch.status,
        startTime: batch.startTime ? dayjs(batch.startTime, 'HH:mm') : null,
        endTime: batch.endTime ? dayjs(batch.endTime, 'HH:mm') : null,
        maxStudents: batch.maxStudents ?? 3,
      });
    }
    if (!isEditMode) {
      setEditedStudentAssignments([]);
      setSelectedStudentForView(null);
    }
  }, [batch, isEditMode, form]);

  useEffect(() => {
    if (batchData?.isSuccess) {
      setIsEditMode(false);
      batchData.isSuccess = false;
      dispatch(batchActions.viewBatch({ id: batchId }));
    }
  }, [batchData?.isSuccess, dispatch, batchId]);

  useEffect(() => {
    if (!availableUsersState?.isSuccess) return;
    const instructors = availableUsersState?.availableUsers?.users ?? [];
    if (pendingUserFetch === 'INSTRUCTOR') {
      setAvailableInstructors(instructors);
      setPendingUserFetch('STUDENT');
    }
  }, [availableUsersState?.isSuccess, pendingUserFetch]);

  useEffect(() => {
    if (!availableStudentsState?.isSuccess) return;
    const students = availableStudentsState?.availableStudents?.users ?? [];
    if (pendingUserFetch === 'STUDENT') {
      setAvailableStudents(students);
      setPendingUserFetch(null);
      setLoadingUsers(false);
    }
  }, [availableStudentsState?.isSuccess, pendingUserFetch]);

  useEffect(() => {
    if (markAttendanceState?.isSuccess && markAttendanceState?.attendance) {
      const results = markAttendanceState.attendance.results || [];
      results.forEach((result: any) => {
        if (!result.success && pendingAttendanceUpdate === result.studentId) {
          setAttendanceData(prev => ({
            ...prev,
            [result.studentId]: previousAttendanceData[result.studentId] || null,
          }));
        }
      });
      dispatch(batchActions.viewBatch({ id: batchId }));
      setPendingAttendanceUpdate(null);
      markAttendanceState.isSuccess = false;
    }
    if (markAttendanceState?.isError) {
      if (pendingAttendanceUpdate && previousAttendanceData[pendingAttendanceUpdate] !== undefined) {
        setAttendanceData(prev => ({
          ...prev,
          [pendingAttendanceUpdate]: previousAttendanceData[pendingAttendanceUpdate],
        }));
      }
      setPendingAttendanceUpdate(null);
      markAttendanceState.isError = false;
    }
  }, [markAttendanceState?.isSuccess, markAttendanceState?.isError, batchId, dispatch, pendingAttendanceUpdate, previousAttendanceData]);

  const handleEditSave = () => {
    form.validateFields().then(values => {
      const normalizedStudentIds = (editedStudentAssignments || []).map((entry: any) => {
        const studentId = typeof entry.studentId === 'object' ? entry.studentId._id : entry.studentId;
        return {
          studentId,
          assignedDates: entry.assignedDates || [],
          enrollmentDaysRemaining: entry.enrollmentDaysRemaining || 0,
          balanceClassDates: entry.balanceClassDates || [],
          assignedAt: entry.assignedAt || new Date().toISOString(),
        };
      });

      dispatch(batchActions.createBatch({
        _id: batch?._id,
        batchName: values.batchName,
        startTime: values.startTime ? values.startTime.format('HH:mm') : batch?.startTime,
        endTime: values.endTime ? values.endTime.format('HH:mm') : batch?.endTime,
        instructorId: values.instructorId || null,
        staffId: values.staffId || batch?.staffId?._id || null,
        trainingType: values.trainingType || batch?.trainingType || null,
        studentIds: isEditMode ? normalizedStudentIds : (Array.isArray(values.studentIds) ? values.studentIds : []),
        status: values.status || batch?.status,
        maxStudents: values.maxStudents ?? batch?.maxStudents ?? 3,
      }));
    });
  };

  const instructorOptions = useMemo(() => {
    const options = [...availableInstructors];
    // For SIMULATION, include staff in the same list
    const currentTrainingType = selectedTrainingType || batch?.trainingType;
    if (currentTrainingType === 'SIMULATION') {
      availableStaff.forEach((staff: any) => {
        if (!options.some((o: any) => o._id === staff._id)) {
          options.push(staff);
        }
      });
    }
    if (batch?.instructorId && typeof batch.instructorId === 'object') {
      const alreadyIncluded = options.some(inst => inst._id === batch.instructorId._id);
      if (!alreadyIncluded) options.unshift(batch.instructorId);
    }
    return options.map(inst => ({ label: `${inst.firstName} ${inst.lastName}`, value: inst._id }));
  }, [availableInstructors, availableStaff, batch, selectedTrainingType]);

  const studentOptions = useMemo(() => {
    const options = [...availableStudents];
    if (batch?.studentIds && Array.isArray(batch.studentIds)) {
      batch.studentIds.forEach((stu: any) => {
        if (typeof stu === 'object' && !options.some(s => s._id === stu._id)) {
          options.push(stu);
        }
      });
    }
    return options.map(stu => ({ label: `${stu.firstName} ${stu.lastName}`, value: stu._id }));
  }, [availableStudents, batch]);

  const handleAttendanceChange = (value: any, record: any) => {
    const isPresent = value.toUpperCase() === 'PRESENT';
    setPreviousAttendanceData(prev => ({
      ...prev,
      [record.id]: attendanceData[record.id] !== undefined ? attendanceData[record.id] : (record.attendanceMarkedToday ? 'PRESENT' : 'ABSENT'),
    }));
    setPendingAttendanceUpdate(record.id);
    setAttendanceData(prev => ({ ...prev, [record.id]: isPresent ? 'PRESENT' : 'ABSENT' }));

    if (isEditMode && batch?._id) {
      dispatch(batchActions.markAttendance({
        batchId: batch._id,
        sessionDate: dayjs().format('YYYY-MM-DD'),
        attendanceRecords: [{ studentId: record.studentID, status: isPresent ? 'PRESENT' : 'ABSENT' }],
      }));
    }
  };

  const getAttendanceValue = (studentId: string, originalValue: boolean): 'PRESENT' | 'ABSENT' => {
    if (attendanceData[studentId] !== undefined && attendanceData[studentId] !== null) {
      return attendanceData[studentId] as 'PRESENT' | 'ABSENT';
    }
    return originalValue ? 'PRESENT' : 'ABSENT';
  };

  const handleAddStudentWithDate = (student: any, assignedDate: string) => {
    if (!batch?._id) {
      message.error('Batch is not loaded');
      return;
    }

    if (isEditMode) {
      const studentId = String(student?._id ?? student?.studentId?._id ?? student?.studentId ?? student);
      const normalizedDateKey = getAssignmentDateKey(assignedDate);
      if (!normalizedDateKey) {
        message.error('Please select a valid date');
        return;
      }

      const maxPerDay = batch?.maxStudents ?? 3;

      setEditedStudentAssignments((prev) => {
        const uniqueStudentIdsOnDate = new Set<string>();
        prev.forEach((item: any) => {
          const itemStudentId = String(
            typeof item.studentId === 'object' ? (item.studentId?._id ?? item.studentId) : item.studentId
          );
          const dates = getAssignmentDates(item);
          if (dates.some((dateValue: any) => getAssignmentDateKey(dateValue) === normalizedDateKey)) {
            uniqueStudentIdsOnDate.add(itemStudentId);
          }
        });

        if (!uniqueStudentIdsOnDate.has(studentId) && uniqueStudentIdsOnDate.size >= maxPerDay) {
          message.warning(`Cannot assign more than ${maxPerDay} students to the same date`);
          return prev;
        }

        const existingIndex = prev.findIndex((item: any) => {
          const itemStudentId = String(
            typeof item.studentId === 'object' ? (item.studentId?._id ?? item.studentId) : item.studentId
          );
          return itemStudentId === studentId;
        });

        if (existingIndex !== -1) {
          const existing = prev[existingIndex];
          const existingDates = getAssignmentDates(existing);
          const existingDateKeys = new Set(existingDates.map((d: any) => getAssignmentDateKey(d)));
          if (existingDateKeys.has(normalizedDateKey)) {
            message.error('Student is already assigned to this date');
            return prev;
          }
          return prev.map((item: any, idx: number) => {
            if (idx !== existingIndex) return item;
            return {
              ...item,
              assignedDates: [...existingDates, buildAssignedDateEntry(assignedDate, batch)],
            };
          });
        }

        return [
          ...prev,
          {
            studentId: student?._id || studentId,
            firstName: student?.firstName,
            lastName: student?.lastName,
            phoneNumber: student?.phoneNumber,
            assignedDates: [buildAssignedDateEntry(assignedDate, batch)],
            assignedAt: new Date().toISOString(),
          },
        ];
      });
      return;
    }

  };

  const handleRemoveStudent = (studentId: string, assignedDate?: string) => {
    if (!batch?._id) {
      message.error('Batch is not loaded');
      return;
    }

    if (isEditMode) {
      setEditedStudentAssignments((prev) => {
        return prev.reduce((acc: any[], item: any) => {
          const id = getAssignmentStudentId(item);
          if (String(id) !== String(studentId)) {
            acc.push(item);
            return acc;
          }

          if (!assignedDate) return acc;

          const existingDates = getAssignmentDates(item);
          const filteredDates = existingDates.filter((d: any) => getAssignmentDateKey(d) !== assignedDate);
          if (filteredDates.length > 0) {
            acc.push({
              ...item,
              assignedDates: filteredDates.map((d: any) => buildAssignedDateEntry(dayjs(d).format('YYYY-MM-DD'), batch)),
            });
          }
          return acc;
        }, []);
      });
      return;
    }
  };

  useEffect(() => {
    if (assignStudentWithDateState?.isSuccess) {
      message.success('Student assigned for selected date');
      dispatch(batchActions.viewBatch({ id: batchId }));
    }
    if (assignStudentWithDateState?.isError) {
      message.error(assignStudentWithDateState.error || 'Unable to assign student');
    }
  }, [assignStudentWithDateState, batchId, dispatch]);

  useEffect(() => {
    if (removeStudentFromBatchState?.isSuccess) {
      message.success('Student removed from batch');
      dispatch(batchActions.viewBatch({ id: batchId }));
    }
    if (removeStudentFromBatchState?.isError) {
      message.error(removeStudentFromBatchState.error || 'Unable to remove student');
    }
  }, [removeStudentFromBatchState, batchId, dispatch]);

  // Unified student list — identity + assigned dates + today's attendance state.
  const mergedStudents = useMemo(() => (batch?.studentIds || []).map((stu: any, index: number) => {
    const studentObject = stu && typeof stu === 'object' && stu.studentId && typeof stu.studentId === 'object'
      ? stu.studentId
      : (stu && typeof stu === 'object' ? stu : null);
    const id = (stu && typeof stu === 'object' ? stu._id : stu) || `student-${index}`;
    const studentID = studentObject._id
    const name = studentObject && (studentObject.firstName || studentObject.lastName)
      ? `${studentObject.firstName || ''} ${studentObject.lastName || ''}`.trim()
      : String(id);
    const phone = studentObject?.phoneNumber || null;
    const dateKeys = getAssignmentDates(stu).map((d: any) => getAssignmentDateKey(d)).filter(Boolean);
    const dates = Array.from(new Set(
      getAssignmentDates(stu)
        .map((d: any) => { const dd = dayjs(d); return dd.isValid() ? dd.format('DD/MM/YYYY') : null; })
        .filter(Boolean)
    ));
    const assignedToday = dateKeys.includes(dayjs().format('YYYY-MM-DD'));
    return { id, studentID, name, phone, dates, assignedToday, attendanceMarkedToday: (stu && typeof stu === 'object' ? stu.attendanceMarkedToday : undefined) ?? false };
  }), [batch?.studentIds]);

  const todayAssignedStudents = useMemo(() => mergedStudents.filter((s: any) => s.assignedToday), [mergedStudents]);

  const attendanceMarkedCount = useMemo(() => todayAssignedStudents.filter((s: any) => s.attendanceMarkedToday).length, [todayAssignedStudents]);
  const todayLabel = dayjs().format('dddd, DD MMM YYYY');

  // date ('YYYY-MM-DD') -> unique students assigned that day, for the availability calendar
  const assignmentsByDate = useMemo(() => {
    const map = new Map<string, { id: string; name: string }[]>();
    (batch?.studentIds || []).forEach((stu: any, index: number) => {
      const studentObject = stu && typeof stu === 'object' && stu.studentId && typeof stu.studentId === 'object'
        ? stu.studentId
        : (stu && typeof stu === 'object' ? stu : null);
      const id = (stu && typeof stu === 'object' ? stu._id : stu) || `student-${index}`;
      const name = studentObject && (studentObject.firstName || studentObject.lastName)
        ? `${studentObject.firstName || ''} ${studentObject.lastName || ''}`.trim()
        : String(id);
      getAssignmentDates(stu).forEach((d: any) => {
        const dateObj = dayjs(d);
        if (!dateObj.isValid()) return;
        const key = dateObj.format('YYYY-MM-DD');
        const list = map.get(key) || [];
        if (!list.some((entry) => entry.id === id)) list.push({ id, name });
        map.set(key, list);
      });
    });
    return map;
  }, [batch?.studentIds]);

  // Filtered assignments for View mode when a student is selected
  const filteredAssignmentsByDate = useMemo(() => {
    if (!selectedStudentForView) return assignmentsByDate;
    
    const filtered = new Map<string, { id: string; name: string }[]>();
    assignmentsByDate.forEach((students, date) => {
      const matchingStudent = students.find(s => s.id === selectedStudentForView);
      if (matchingStudent) {
        filtered.set(date, [matchingStudent]);
      }
    });
    return filtered;
  }, [assignmentsByDate, selectedStudentForView]);

  if (isLoading) {
    return (
      <div className='view-loading'>
        <SpinnerLoader />
      </div>
    );
  }

  if (!batch && !isLoading) {
    return (
      <div className="entity-view-empty">
        <Empty description="Batch not found" />
        <Button type="primary" onClick={() => navigate('/batches')} style={{ marginTop: 16 }}>Back</Button>
      </div>
    );
  }

  const instructorName = batch?.instructorId && typeof batch.instructorId === 'object' ? `${batch.instructorId.firstName ?? ''} ${batch.instructorId.lastName ?? ''}`.trim() : null;
  const studentsCount = isEditMode
    ? new Set(
        (Array.isArray(activeStudentAssignments) ? activeStudentAssignments : [])
          .map((item: any) => String(getAssignmentStudentId(item)))
      ).size
    : todayAssignedStudents.length;
  const capacity = isEditMode ? (watchedMaxStudents ?? batch?.maxStudents ?? 3) : (batch?.maxStudents ?? 3);
  const canEdit = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;
  const statusKey = (batch?.status || '').toUpperCase();
  const statusClass = statusKey === 'ACTIVE' ? 'active' : statusKey === 'COMPLETED' ? 'done' : 'pending';
  const createdLabel = batchCreatedDateObj
    ? batchCreatedDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const batchNavState = { batchId, batchName: batch?.batchName, trainingType: batch?.trainingType };
  const goToTripSheets = () => navigate('/batches/tripsheets', { state: batchNavState });
  const goToTracking = () => navigate('/batches/tracking', { state: batchNavState });
  const goToHistory = () => navigate('/batches/history', { state: batchNavState });

  return (
    <div className="batch-view-page user-view-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/batches')} className="batch-back-btn">Batches</Button>
        <div className="batch-header-right">
          {createdLabel && <span className="batch-created">Created {createdLabel}</span>}
          {!isEditMode ? (
            canEdit && (
              <GuardedButton className="batch-edit-btn" icon={<EditOutlined />} onClick={() => setIsEditMode(true)}>Edit Batch</GuardedButton>
            )
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={() => setIsEditMode(false)}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={isUpdating} onClick={handleEditSave}>Save</Button>
            </Space>
          )}
        </div>
      </div>

      {/* Title / hero */}
      <div className="batch-hero">
        <div className="batch-hero-icon"><CalendarOutlined /></div>
        <div className="batch-hero-info">
          <div className="batch-hero-titlerow">
            <h2 className="batch-hero-name">{batch?.batchName || '-'}</h2>
            <span className={`batch-status-pill batch-status-pill--${statusClass}`}>
              <span className="batch-status-dot" />
              {batch?.status || '-'}
            </span>
          </div>
          <div className="batch-hero-meta">
            <span className="batch-hero-meta-item"><TeamOutlined /> {studentsCount} / {capacity} Students</span>
            <span className="batch-hero-meta-item"><ClockCircleOutlined /> {batch?.startTime || '--'} - {batch?.endTime || '--'}</span>
          </div>
        </div>
      </div>

      {/* Detail sections */}
      <Form form={form} layout="vertical" className="batch-view-form">
        {/* ── Batch Details — Personal-details style card ── */}
        <section className="uv-section uv-section--divider">
          <div className="uv-card-header">
            <InfoCircleOutlined className="uv-card-header-anticon" />
            <span className="uv-card-header-title">Batch Details</span>
          </div>
          {isEditMode ? (
            <div className="uv-personal-grid batch-details-edit-grid">
              <div className="uv-pd-item">
                <span className="uv-pd-label">Batch Name</span>
                <Form.Item name="batchName" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please enter batch name' }]}>
                  <Input placeholder="Enter batch name" />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Max Students</span>
                <Form.Item name="maxStudents" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please enter max students' }]}>
                  <Input placeholder="Enter max students" />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Status</span>
                <Form.Item name="status" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please select status' }]}>
                  <Select placeholder="Select status">
                    <Select.Option value="ACTIVE">Active</Select.Option>
                    <Select.Option value="INACTIVE">Inactive</Select.Option>
                    <Select.Option value="COMPLETED">Completed</Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Start Time</span>
                <Form.Item name="startTime" style={{ marginBottom: 0 }} tooltip="Session time is locked and cannot be changed once the batch is created">
                  <TimePicker format="HH:mm" placeholder="Select start time" style={{ width: '100%' }} disabled />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">End Time</span>
                <Form.Item name="endTime" style={{ marginBottom: 0 }} tooltip="Session time is locked and cannot be changed once the batch is created">
                  <TimePicker format="HH:mm" placeholder="Select end time" style={{ width: '100%' }} disabled />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Training Type</span>
                <Form.Item name="trainingType" style={{ marginBottom: 0 }}>
                  <Select placeholder="Select training type" allowClear onChange={(value: string | undefined) => {
                    setSelectedTrainingType(value || null);
                    form.setFieldsValue({ instructorId: undefined });
                  }}>
                    <Select.Option value="SIMULATION">Simulation</Select.Option>
                    <Select.Option value="ON_ROAD">On-Road</Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <Form.Item noStyle shouldUpdate={(prev: any, cur: any) => prev.trainingType !== cur.trainingType}>
                {() => {
                  const currentType = selectedTrainingType || batch?.trainingType;
                  const label = currentType === 'SIMULATION' ? 'Instructor / Staff' : 'Instructor';
                  const placeholder = loadingUsers ? 'Loading...' : (currentType === 'SIMULATION' ? 'Select instructor or staff' : 'Select instructor');
                  return (
                    <div className="uv-pd-item">
                      <span className="uv-pd-label">{label}</span>
                      <Form.Item name="instructorId" style={{ marginBottom: 0 }}>
                        <Select placeholder={placeholder} allowClear options={instructorOptions} />
                      </Form.Item>
                    </div>
                  );
                }}
              </Form.Item>
            </div>
          ) : (
            <div className="uv-personal-grid">
              <div className="uv-pd-item">
                <span className="uv-pd-label">Batch Name</span>
                <span className="uv-pd-value">{batch?.batchName || '-'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Session Time</span>
                <span className="uv-pd-value">{batch?.startTime || '--'} — {batch?.endTime || '--'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Max Students</span>
                <span className="uv-pd-value">{batch?.maxStudents || '-'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">{batch?.trainingType === 'SIMULATION' ? 'Instructor / Staff' : 'Instructor'}</span>
                <span className="uv-pd-value">
                  {instructorName ? (
                    <span className="batch-instructor-chip">
                      <Avatar size={22} className="batch-instructor-av">{instructorName.charAt(0).toUpperCase()}</Avatar>
                      {instructorName}
                    </span>
                  ) : <span className="batch-prop-muted">Not assigned</span>}
                </span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Training Type</span>
                <span className="uv-pd-value">
                  {batch?.trainingType ? (
                    <span className={`batch-type-pill batch-type-pill--${batch.trainingType === 'SIMULATION' ? 'sim' : 'road'}`}>
                      <span className="batch-type-dot" />
                      {batch.trainingType === 'SIMULATION' ? 'SIMULATION' : 'ON-ROAD'}
                    </span>
                  ) : <span className="batch-prop-muted">Not set</span>}
                </span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Status</span>
                <span className="uv-pd-value">
                  <span className={`batch-status-pill batch-status-pill--${statusClass}`}>
                    <span className="batch-status-dot" />{batch?.status || '-'}
                  </span>
                </span>
              </div>
            </div>
          )}
        </section>

        {/* ── Student info (40%) beside the availability calendar (60%) ── */}
        <div className={`batch-students-row${!isEditMode ? ' batch-students-row--split' : ''}`}>
          <section className="uv-section uv-section--divider batch-students-col">
            <div className="uv-card-header uv-card-header--spread">
              <span className="uv-card-header-left">
                <TeamOutlined className="uv-card-header-anticon" />
                <span className="uv-card-header-title">Students ({studentsCount}/{capacity})</span>
              </span>
              <span className="batch-attendance-date"><CalendarOutlined /> {todayLabel}</span>
            </div>

            {isEditMode && (
              <div className="batch-students-editpicker">
                <StudentDatePicker
                  batchId={batch?._id}
                  startTime={batch?.startTime || ''}
                  endTime={batch?.endTime || ''}
                  isOnRoad={batch?.trainingType === 'ON_ROAD'}
                  assignedStudents={Array.isArray(activeStudentAssignments) ? activeStudentAssignments : []}
                  onStudentDateAdd={handleAddStudentWithDate}
                  onStudentRemove={handleRemoveStudent}
                  layout={'horizontal'}
                  maxStudentsPerDay={watchedMaxStudents ?? batch?.maxStudents ?? 3}
                />
              </div>
            )}

            {mergedStudents.length > 0 ? (
              <>
                {!isEditMode && canEdit && attendanceMarkedCount < todayAssignedStudents.length && (
                  <div className="batch-attendance-hint">
                   <label><InfoCircleOutlined /> Use <span style={{ fontWeight: 'bold', padding: '0 4px' }}>Edit Batch</span> to mark today's attendance.</label>  
                  </div>
                )}
                <div className="batch-attendance-list">
                  <div >
                    <>
                      Attendance marked for {attendanceMarkedCount} of {todayAssignedStudents.length} students today.</>
                  </div>
                  {(isEditMode ? mergedStudents : todayAssignedStudents).map((record: any) => {
                    const currentValue = getAttendanceValue(record.id, record.attendanceMarkedToday);
                    const isMarked = !!record.attendanceMarkedToday;
                    const touched = attendanceData[record.id] !== undefined && attendanceData[record.id] !== null;
                    return (
                      <div className="batch-attendance-row" key={record.id}>
                        <div className="batch-attendance-user">
                          <Avatar size={40} className="batch-student-av">{ (record.name || 'S')
                        .split(' ').filter(Boolean).map((part:any) => part[0]).join('').slice(0, 2).toUpperCase()}</Avatar>
                          <div className="batch-student-meta">
                            <span className="batch-student-name">{record.name || 'Student'}</span>
                            {record.phone && <span className="batch-student-sub">{record.phone}</span>}
                            {record.dates.length > 0 && (() => {
                              const isExpanded = expandedAttendanceRows[record.id];
                              return (
                                <div
                                  className={`batch-student-dates${isExpanded ? ' batch-student-dates--expanded' : ' batch-student-dates--collapsed'}`}
                                  onClick={() => toggleAttendanceRow(record.id)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span className="batch-student-dates-label">
                                    Assigned {isExpanded ? '▲' : '▼'}
                                  </span>
                                  {isExpanded ? (
                                    <>
                                      {record.dates.slice(0, 4).map((d: string) => (
                                        <span className="batch-date-chip" key={d}>{d}</span>
                                      ))}
                                      {record.dates.length > 4 && (
                                        <span
                                          className="batch-date-chip batch-date-chip--more"
                                          title={record.dates.slice(4).join(', ')}
                                        >
                                          +{record.dates.length - 4} more
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="batch-date-chip batch-date-chip--summary">
                                      {record.dates.length} assigned date{record.dates.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="batch-attendance-action">

                          {isMarked ? (
                            <span className={`batch-att-status batch-att-status--${currentValue === 'PRESENT' ? 'present' : 'absent'}`}>
                              {currentValue === 'PRESENT' ? <CheckCircleFilled /> : <CloseCircleFilled />}
                              {currentValue === 'PRESENT' ? 'Present' : 'Absent'}
                            </span>
                          ) : isEditMode ? (
                            <div className="batch-att-editcell">
                              {!touched && <span className="batch-att-notmarked">Not marked</span>}
                              <Segmented
                                className={`batch-att-seg${touched ? ` batch-att-seg--${currentValue.toLowerCase()}` : ' batch-att-seg--untouched'}`}
                                options={['PRESENT', 'ABSENT']}
                                value={touched ? currentValue : ''}
                                onChange={value => handleAttendanceChange(value, record)}
                              />
                            </div>
                          ) : (
                            <span className="batch-att-status batch-att-status--pending">Not marked</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="batch-students-empty">
                <div className="batch-empty-ic"><TeamOutlined /></div>
                <div className="batch-empty-title">No students enrolled yet</div>
                <div className="batch-empty-sub">Add up to {capacity} students to this batch.</div>
                {canEdit && !isEditMode && (
                  <Button className="batch-addstudent-btn" icon={<PlusOutlined />} onClick={() => setIsEditMode(true)} style={{ marginTop: 14 }}>Add student</Button>
                )}
              </div>
            )}
          </section>

          {/* Availability calendar — right 60%, shows which students are assigned on each date */}
          {!isEditMode && (
            <section className="uv-section uv-section--divider batch-availability-col">
              <div className="uv-card-header uv-card-header--spread">
                <span className="uv-card-header-left">
                  <CalendarOutlined className="uv-card-header-anticon" />
                  <span className="uv-card-header-title">Student Availability</span>
                </span>
                <span className="batch-attendance-date"><TeamOutlined /> {selectedStudentForView ? 'Selected student' : `${studentsCount} assigned`}</span>
              </div>
              
              {mergedStudents.length > 0 && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                  <Select
                    placeholder="Filter by student (optional)"
                    allowClear
                    value={selectedStudentForView}
                    onChange={setSelectedStudentForView}
                    style={{ width: '100%' }}
                    options={mergedStudents.map((student : any ) => ({
                      label: student.name,
                      value: student.id,
                    }))}
                  />
                </div>
              )}
              
              <div className="batch-availability-calendar">
                <Calendar
                  fullscreen={false}
                  value={calMonth}
                  onChange={setCalMonth}
                  headerRender={({ value }) => (
                    <div className="bav-cal-header">
                      <span className="bav-cal-month">{value.format('MMMM YYYY')}</span>
                      <span className="bav-cal-nav">
                        <Button type="text" size="small" onClick={() => setCalMonth(value.subtract(1, 'month'))}>‹</Button>
                        <Button type="text" size="small" onClick={() => setCalMonth(value.add(1, 'month'))}>›</Button>
                      </span>
                    </div>
                  )}
                  fullCellRender={(date) => {
                    const key = date.format('YYYY-MM-DD');
                    const list = filteredAssignmentsByDate.get(key) || [];
                    const isToday = date.isSame(dayjs(), 'day');
                    const avatars = list.slice(0, 3).map((entry, i) => {
                      const initials = (entry.name || 'S')
                        .split(' ').filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
                      return <span key={`${key}-${i}`} className="bav-avatar" title={entry.name}>{initials}</span>;
                    });
                    const cell = (
                      <div className={['bav-cell', list.length ? 'bav-cell--has' : '', isToday ? 'bav-cell--today' : ''].filter(Boolean).join(' ')} title="">
                        <span className="bav-cell__day">{date.date()}</span>
                        {avatars.length > 0 && (
                          <span className="bav-cell__avatars">
                            {avatars}
                            {list.length > 3 && <span className="bav-avatar bav-avatar--more">+{list.length - 3}</span>}
                          </span>
                        )}
                      </div>
                    );
                    return list.length > 0 ? (
                      <Tooltip placement="top" title={<div>{list.map((entry, i) => <div key={i}>{entry.name}</div>)}</div>}>
                        {cell}
                      </Tooltip>
                    ) : cell;
                  }}
                />
              </div>
            </section>
          )}
        </div>

        {/* Quick links — History, Trip Sheets & Tracking as full-width navigation tiles */}
        {!isEditMode && (
          <div className="batch-nav-cards">
            <div className="batch-nav-card" onClick={goToHistory} role="button" tabIndex={0}>
              <span className="batch-nav-card__icon batch-nav-card__icon--history"><HistoryOutlined /></span>
              <div className="batch-nav-card__body">
                <span className="batch-nav-card__title">History</span>
                <span className="batch-nav-card__sub">Instructor assignments & session activity</span>
              </div>
              <EyeOutlined className="batch-nav-card__arrow" />
            </div>

            <div className="batch-nav-card" onClick={goToTripSheets} role="button" tabIndex={0}>
              <span className="batch-nav-card__icon batch-nav-card__icon--tripsheets"><FileTextOutlined /></span>
              <div className="batch-nav-card__body">
                <span className="batch-nav-card__title">Trip Sheets</span>
                <span className="batch-nav-card__sub">On-road trip logs, odometer & distance</span>
              </div>
              <EyeOutlined className="batch-nav-card__arrow" />
            </div>

            {pageAccess.isLocationTracking && (
              <div className="batch-nav-card" onClick={goToTracking} role="button" tabIndex={0}>
                <span className="batch-nav-card__icon batch-nav-card__icon--tracking"><EnvironmentOutlined /></span>
                <div className="batch-nav-card__body">
                  <span className="batch-nav-card__title">Live Tracking</span>
                  <span className="batch-nav-card__sub">Follow the vehicle & route on the map</span>
                </div>
                <EyeOutlined className="batch-nav-card__arrow" />
              </div>
            )}
          </div>
        )}
      </Form>
    </div>
  );
};

export default BatchView;
